import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Container
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  NotificationsOutlined, 
  SearchOutlined,
  Person as PersonIcon 
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useUserType } from '../contexts/UserTypeContext';
import ReviewerDrawer from './drawers/ReviewerDrawer';
import AdvertiserDrawer from './drawers/AdvertiserDrawer';

interface HeaderProps {
  showIcons?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showIcons = false }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentType, setCurrentType } = useUserType();
  const location = useLocation();

  // Check if current path is in mypage
  const isMyPage = location.pathname.includes('/mypage');
  
  // Determine user type from URL if in mypage
  React.useEffect(() => {
    if (isMyPage) {
      const pathType = location.pathname.includes('/reviewer') ? 'reviewer' : 'advertiser';
      if (currentType !== pathType) {
        setCurrentType(pathType);
      }
    }
  }, [location.pathname, currentType, setCurrentType, isMyPage]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <AppBar 
      position="fixed" 
      color="default" 
      elevation={1}
      sx={{ 
        bgcolor: 'white',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Toolbar>
        <Container 
          maxWidth="md" 
          disableGutters 
          sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 1.5, sm: 3 }
          }}
        >
          <Link 
            to="/" 
            style={{ 
              textDecoration: 'none', 
              color: 'inherit',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Typography 
              variant="h6" 
              component="div"
              sx={{ 
                fontWeight: 700,
                fontSize: isMobile ? '1.125rem' : '1.25rem'
              }}
            >
              Smart Review
            </Typography>
          </Link>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="notifications"
            >
              <NotificationsOutlined />
            </IconButton>
            {showIcons && (
              <>
                <IconButton
                  size="large"
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleDrawerToggle}
                  sx={{
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Container>
      </Toolbar>

      {currentType === 'reviewer' ? (
        <ReviewerDrawer open={drawerOpen} onClose={handleDrawerClose} />
      ) : (
        <AdvertiserDrawer open={drawerOpen} onClose={handleDrawerClose} />
      )}
    </AppBar>
  );
};

export default Header; 